import { useState, useEffect } from "react";
import { z } from "zod";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Upload, Image, Palette, Eye } from "lucide-react";
import BrandColorSelector from "@/components/settings/BrandColorSelector";
import ChatBackgroundSelector from "@/components/settings/ChatBackgroundSelector";
import { updateOrganizationChatBackground } from "@/services/organization";
import { Switch } from "@/components/ui/switch";
import SettingsContentWrapper, {
  SettingsBlock,
} from "./SettingsContentWrapper";
import ChatInterfacePreview from "@/components/previews/ChatInterfacePreview";

// Appearance settings schema
const appearanceSchema = z.object({
  brand_color: z
    .string()
    .regex(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/, {
      message: "Please enter a valid hex color code (e.g., #3B82F6)",
    })
    .default("#3B82F6")
    .optional(),
  display_hubflo_branding: z.boolean().default(true),
});

type FormValues = z.infer<typeof appearanceSchema>;

const AppearanceSettingsContent = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState<"logo" | "banner" | null>(
    null,
  );
  const { organization, refreshProfile } = useAuth();
  const { toast } = useToast();

  // State for live preview updates
  const [previewSettings, setPreviewSettings] = useState({
    brandColor: organization?.brand_color || "#3B82F6",
    logoUrl: organization?.logo_url || "",
    showHubfloBranding:
      (organization as any)?.display_hubflo_branding !== false,
    chatBackground: (organization as any)?.chat_background || "default",
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(appearanceSchema),
    defaultValues: {
      brand_color: organization?.brand_color || "#3B82F6",
      display_hubflo_branding: organization?.display_hubflo_branding !== false, // default to true if undefined
    },
  });

  // Watch form values for live preview updates
  const watchedValues = useWatch({
    control: form.control,
    defaultValue: {
      brand_color: organization?.brand_color || "#3B82F6",
      display_hubflo_branding:
        (organization as any)?.display_hubflo_branding !== false,
    },
  });

  // Update preview settings whenever watched values change
  useEffect(() => {
    setPreviewSettings({
      brandColor: watchedValues.brand_color || "#3B82F6",
      logoUrl: organization?.logo_url || "",
      showHubfloBranding: watchedValues.display_hubflo_branding !== false,
      chatBackground: (organization as any)?.chat_background || "default",
    });
  }, [
    watchedValues,
    organization?.logo_url,
    (organization as any)?.chat_background,
  ]);

  // Update form values when organization data changes
  useEffect(() => {
    if (organization) {
      form.reset({
        brand_color: organization.brand_color || "#3B82F6",
        display_hubflo_branding: organization.display_hubflo_branding !== false, // default to true if undefined
      });
    }
  }, [organization, form]);

  // Update preview when organization data changes (logo uploads)
  useEffect(() => {
    if (organization) {
      setPreviewSettings((prev) => ({
        ...prev,
        logoUrl: organization.logo_url || "",
        showHubfloBranding:
          (organization as any)?.display_hubflo_branding !== false,
        chatBackground: (organization as any)?.chat_background || "default",
      }));
    }
  }, [organization]);

  const onSubmit = async (data: FormValues) => {
    if (!organization?.id) return false;

    setIsLoading(true);
    try {
      // Only update the brand color, not the logo/banner files here
      const { error: updateError } = await supabase
        .from("organizations")
        .update({
          brand_color: data.brand_color || null,
          display_hubflo_branding: data.display_hubflo_branding,
        })
        .eq("id", organization.id);

      if (updateError) throw updateError;

      // Refresh the profile to get the updated organization data
      await refreshProfile();

      toast({
        title: "Appearance updated",
        description: "Your appearance settings have been saved successfully.",
      });

      return true;
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Failed to update appearance",
        description: error.message || "An unexpected error occurred.",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Handle file uploads for logo and banner
  const handleFileUpload = async (fileType: "logo" | "banner", file: File) => {
    if (!organization?.id) {
      toast({
        variant: "destructive",
        title: "Upload failed",
        description:
          "Organization ID not found. Please refresh the page and try again.",
      });
      return;
    }

    setIsUploading(fileType);

    try {
      // Validate the file
      const maxSize = 6; // 6MB max for all files

      // Validate file type with specific MIME types
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/svg+xml",
        "image/webp",
      ];

      if (!allowedTypes.includes(file.type)) {
        throw new Error(
          "Invalid file type. Please upload JPG, PNG, GIF, SVG, or WebP images.",
        );
      }

      if (file.size > maxSize * 1024 * 1024) {
        throw new Error(`File size exceeds ${maxSize}MB limit.`);
      }

      // Upload the file to storage with safe file extension handling
      const fileExt = file.name.split(".").pop()?.toLowerCase();
      const validExtensions = ["jpg", "jpeg", "png", "gif", "svg", "webp"];
      const safeExtension =
        fileExt && validExtensions.includes(fileExt) ? fileExt : "jpg";
      const fileName = `${organization.id}-${fileType}-${Date.now()}.${safeExtension}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("organization_logos")
        .upload(fileName, file, {
          upsert: true,
          contentType: file.type,
        });

      if (uploadError) {
        console.error("Storage upload error:", uploadError);
        throw new Error(`Error uploading image: ${uploadError.message}`);
      }

      // Get the public URL
      const { data: publicUrlData } = supabase.storage
        .from("organization_logos")
        .getPublicUrl(fileName);

      if (!publicUrlData || !publicUrlData.publicUrl) {
        throw new Error("Failed to get public URL for uploaded file");
      }

      const fileUrl = publicUrlData.publicUrl;

      // Update the organization with the new URL
      const updateData =
        fileType === "logo" ? { logo_url: fileUrl } : { banner_url: fileUrl };

      const { error: updateError } = await supabase
        .from("organizations")
        .update(updateData)
        .eq("id", organization.id);

      if (updateError) {
        console.error("Organization update error:", updateError);
        throw new Error(`Error updating organization: ${updateError.message}`);
      }

      // Refresh the profile to get the updated organization data
      await refreshProfile();

      toast({
        title: `${fileType.charAt(0).toUpperCase() + fileType.slice(1)} updated`,
        description: `Your ${fileType} has been uploaded successfully.`,
      });
    } catch (error: any) {
      console.error(`${fileType} upload error:`, error);
      toast({
        variant: "destructive",
        title: `Failed to upload ${fileType}`,
        description: error.message || "An unexpected error occurred.",
      });
    } finally {
      setIsUploading(null);
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      handleFileUpload("logo", file);
    }
  };

  const handleBannerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      handleFileUpload("banner", file);
    }
  };

  const handleChatBackgroundChange = async (background: string) => {
    if (!organization?.id) return;
    try {
      await updateOrganizationChatBackground(organization.id, background);
      await refreshProfile();
      toast({
        title: "Background updated",
        description: "Chat background has been updated successfully.",
      });
    } catch (error: any) {
      console.error("Error updating chat background:", error);
      toast({
        variant: "destructive",
        title: "Failed to update background",
        description: error.message || "An unexpected error occurred.",
      });
    }
  };

  return (
    <div className="max-w-7xl">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900">
          Assistants Appearance
        </h2>
        <p className="text-gray-600 mt-2">
          Customize your organization's visual identity and branding
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 min-h-0">
        {/* Left column - Settings */}
        <div className="xl:col-span-1 space-y-6 min-w-0">
          {/* Brand Assets */}
          <SettingsBlock
            icon={<Image className="h-5 w-5" />}
            title="Brand Assets"
            iconBgColor="bg-blue-100"
            iconTextColor="text-blue-500"
          >
            {/* Logo Upload */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Logo
                </label>
                <p className="text-sm text-gray-600 mt-1">
                  Recommended size: 200x200px (square), max 6MB (PNG, JPG, SVG,
                  WebP)
                </p>
              </div>
              <div className="flex items-center space-x-4">
                {organization?.logo_url ? (
                  <div className="flex items-center space-x-4">
                    <img
                      src={organization.logo_url}
                      alt="Current logo"
                      className="h-16 w-24 object-contain border rounded-lg bg-gray-50"
                    />
                  </div>
                ) : (
                  <div className="h-16 w-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                    <Image className="h-6 w-6 text-gray-400" />
                  </div>
                )}
                <label htmlFor="logo-upload" className="cursor-pointer">
                  <div className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
                    <Upload className="h-4 w-4" />
                    <span className="text-sm">
                      {isUploading === "logo" ? "Uploading..." : "Upload Logo"}
                    </span>
                  </div>
                  <input
                    id="logo-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleLogoChange}
                    disabled={isUploading === "logo"}
                  />
                </label>
              </div>
            </div>

            {/* Banner Upload */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Add a banner to your agent chat interface (optional)
                </label>
                <p className="text-sm text-gray-600 mt-1">
                  Recommended size: 1200x300px, max 6MB (PNG, JPG, SVG, WebP)
                </p>
              </div>
              <div className="flex items-center space-x-4">
                {organization?.banner_url ? (
                  <div className="flex items-center space-x-4">
                    <img
                      src={organization.banner_url}
                      alt="Current banner"
                      className="h-16 w-32 object-cover border rounded-lg bg-gray-50"
                    />
                  </div>
                ) : (
                  <div className="h-16 w-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                    <Image className="h-6 w-6 text-gray-400" />
                  </div>
                )}
                <label htmlFor="banner-upload" className="cursor-pointer">
                  <div className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
                    <Upload className="h-4 w-4" />
                    <span className="text-sm">
                      {isUploading === "banner"
                        ? "Uploading..."
                        : "Upload Banner"}
                    </span>
                  </div>
                  <input
                    id="banner-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleBannerChange}
                    disabled={isUploading === "banner"}
                  />
                </label>
              </div>
            </div>
          </SettingsBlock>

          {/* Colors and Branding */}
          <SettingsBlock
            icon={<Palette className="h-5 w-5" />}
            title="Colors & Branding"
            iconBgColor="bg-purple-100"
            iconTextColor="text-purple-500"
          >
            <p className="text-sm text-gray-600 mb-4">
              Customize colors and branding options
            </p>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6"
              >
                <FormField
                  control={form.control}
                  name="brand_color"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-700">
                        Brand Color
                      </FormLabel>
                      <FormControl>
                        <BrandColorSelector
                          value={field.value || "#3B82F6"}
                          onChange={field.onChange}
                          disabled={isLoading}
                        />
                      </FormControl>
                      <p className="text-sm text-gray-500 mt-2">
                        Used for buttons, links, and accent elements
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Hidden Hubflo Branding field - keeping for data persistence but not showing to user */}
                <FormField
                  control={form.control}
                  name="display_hubflo_branding"
                  render={({ field }) => (
                    <input
                      type="hidden"
                      value={field.value ? "true" : "false"}
                      onChange={() => {}}
                    />
                  )}
                />

                <div className="flex justify-start">
                  <Button type="submit" disabled={isLoading}>
                    {isLoading ? "Saving..." : "Update brand color"}
                  </Button>
                </div>
              </form>
            </Form>

            {/* AI Assistant Background - Outside of form since it auto-saves */}
            <div className="space-y-3 mt-6">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  AI Assistant Background
                </label>
                <p className="text-sm text-gray-600 mt-1">
                  Choose a background pattern for your chat interfaces
                </p>
              </div>
              <ChatBackgroundSelector
                organizationId={organization?.id || ""}
                currentBackground={organization?.chat_background || "none"}
                onBackgroundChange={handleChatBackgroundChange}
              />
            </div>
          </SettingsBlock>
        </div>

        {/* Right column - Preview */}
        <div className="xl:col-span-1 min-w-0">
          <div className="sticky top-8">
            <div className="space-y-4">
              <ChatInterfacePreview
                brandColor={previewSettings.brandColor}
                logoUrl={previewSettings.logoUrl}
                showHubfloBranding={previewSettings.showHubfloBranding}
                chatBackground={previewSettings.chatBackground}
                agentName="AI Assistant"
                organizationName={
                  (organization as any)?.name || "Your Organization"
                }
                organization={organization}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppearanceSettingsContent;
